import { Box, Button, Flex, IconButton, Image, Text, chakra } from '@chakra-ui/react';
import type { ProductAnnouncement } from '@fluenceur/types';
import { useReducedMotion } from 'framer-motion';
import { ArrowRight, NavArrowLeft, NavArrowRight, Xmark } from 'iconoir-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

interface FeatureAnnouncementPanelProps {
  announcement: ProductAnnouncement;
  position: number;
  total: number;
  /** Closed keeps the card mounted so open/close can animate. */
  open: boolean;
  /** Permanently dismisses the announcement currently on screen. */
  onDismiss: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onSelect: (index: number) => void;
}

/** Duration of the fade applied when the visible slide changes. */
const SLIDE_FADE_MS = 130;

function isSafeInternalPath(value: string): boolean {
  const applicationOrigin = 'https://fluenceur.invalid';
  const hasUnsafeCharacter = [...value].some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return character === '\\' || codePoint <= 0x1f || codePoint === 0x7f;
  });
  if (!value.startsWith('/') || value.startsWith('//') || hasUnsafeCharacter) {
    return false;
  }

  try {
    return new URL(value, applicationOrigin).origin === applicationOrigin;
  } catch {
    return false;
  }
}

function isSafeExternalUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Compact, non-modal product update panel on a dark floating surface. It never
 * traps or moves focus and deliberately has no backdrop: operators can keep
 * working while reading it.
 *
 * The card stays mounted while closed (opacity + translate only) so the
 * top-bar indicator can reopen it without a remount flash.
 */
export function FeatureAnnouncementPanel({
  announcement,
  position,
  total,
  open,
  onDismiss,
  onPrevious,
  onNext,
  onSelect,
}: FeatureAnnouncementPanelProps) {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const locale = (i18n.resolvedLanguage ?? i18n.language).toLowerCase().startsWith('fr')
    ? 'fr'
    : 'en';
  const content = announcement.content[locale] ?? announcement.content.en;
  const titleId = `product-announcement-${announcement.id}-title`;
  const descriptionId = `product-announcement-${announcement.id}-description`;
  const safeInternalLink = announcement.linkUrl ? isSafeInternalPath(announcement.linkUrl) : false;
  const safeExternalLink = announcement.linkUrl ? isSafeExternalUrl(announcement.linkUrl) : false;
  const showAction = Boolean(
    content.linkLabel && announcement.linkUrl && (safeInternalLink || safeExternalLink)
  );

  // Slide changes fade the copy back in rather than swapping it hard. `fading`
  // pins opacity to 0 for one frame; the transition below then plays the
  // 0 -> 1 fade over SLIDE_FADE_MS.
  const [fading, setFading] = useState(false);
  const renderedId = useRef(announcement.id);

  useEffect(() => {
    if (renderedId.current === announcement.id) return;
    renderedId.current = announcement.id;
    if (reduceMotion) return;

    setFading(true);
    const frame = window.requestAnimationFrame(() => setFading(false));
    return () => window.cancelAnimationFrame(frame);
  }, [announcement.id, reduceMotion]);

  const atStart = position <= 1;
  const atEnd = position >= total;
  /**
   * Deliberate departure from the maquette, which always carries three slides
   * and therefore only ever dims the boundary arrows. A lone announcement has
   * nothing to navigate: both arrows would be inert, the single dot would
   * indicate nothing and the counter would read "1 of 1". The whole group goes
   * away instead. With several announcements the maquette behaviour stands.
   */
  const showNavigation = total > 1;

  const arrowStyle = (disabled: boolean) => ({
    w: '26px',
    h: '26px',
    minW: '26px',
    borderRadius: 'full' as const,
    bg: 'announcement.controlBg',
    color: disabled ? 'announcement.controlDisabled' : 'announcement.controlFg',
    // No wrap-around: the boundary arrow is inert, not just visually dimmed.
    pointerEvents: disabled ? ('none' as const) : ('auto' as const),
    _hover: disabled
      ? {}
      : { bg: 'announcement.controlHoverBg', color: 'announcement.pagerActive' },
    _focusVisible: { outline: 'none', boxShadow: 'none' },
  });

  const dismissButtonStyle = {
    w: '28px',
    h: '28px',
    minW: '28px',
    borderRadius: 'full' as const,
    bg: 'announcement.closeBg',
    borderWidth: '1px',
    borderColor: 'announcement.closeBorder',
    color: 'announcement.title',
    _hover: { bg: 'announcement.ctaBg', color: 'announcement.ctaFg' },
    _focusVisible: { outline: 'none', boxShadow: 'none' },
  };

  return (
    <Box
      as="section"
      role="region"
      aria-label={t('announcements.product.regionLabel')}
      aria-describedby={descriptionId}
      aria-live="polite"
      aria-atomic="true"
      aria-hidden={open ? undefined : true}
      position="fixed"
      zIndex="1200"
      right={{ base: '12px', md: '20px' }}
      left={{ base: '12px', md: 'auto' }}
      bottom={{ base: '12px', md: '20px' }}
      w={{ base: 'auto', md: '428px' }}
      maxH="calc(100dvh - 24px)"
      overflow="hidden"
      bg="announcement.surface"
      borderWidth="1px"
      borderColor="announcement.surfaceBorder"
      borderRadius="2xl"
      boxShadow="announcement.panel"
      opacity={open ? 1 : 0}
      transform={open ? 'translateY(0)' : 'translateY(14px)'}
      visibility={open ? 'visible' : 'hidden'}
      pointerEvents={open ? 'auto' : 'none'}
      transition={
        reduceMotion
          ? undefined
          : 'opacity .22s ease, transform .28s cubic-bezier(.2,.8,.2,1), visibility .28s'
      }
    >
      {announcement.visualUrl ? (
        <Box
          position="relative"
          h="196px"
          p="14px"
          bg="announcement.visualBg"
          borderBottomWidth="1px"
          borderColor="announcement.visualBorder"
          overflow="hidden"
        >
          {/* Decorative lilac bloom bleeding out of the top-left corner. */}
          <Box
            aria-hidden="true"
            position="absolute"
            top="-140px"
            left="-120px"
            w="320px"
            h="320px"
            borderRadius="full"
            bg="announcement.visualHalo"
            filter="blur(6px)"
            pointerEvents="none"
          />
          <Image
            src={announcement.visualUrl}
            alt={content.visualAlt ?? ''}
            loading="lazy"
            decoding="async"
            position="relative"
            w="full"
            h="full"
            objectFit="cover"
            borderRadius="lg"
          />
          <IconButton
            aria-label={t('announcements.product.close')}
            onClick={onDismiss}
            position="absolute"
            top="14px"
            right="14px"
            {...dismissButtonStyle}
          >
            <Xmark aria-hidden="true" width="14px" height="14px" />
          </IconButton>
        </Box>
      ) : null}

      <Box px="18px" pt="18px" pb="18px">
        <Flex align="center" justify="space-between" gap="12px" mb="14px">
          {/* Meta chip. The maquette also carries a product-zone segment after
              a vertical rule; `ProductAnnouncement` exposes no zone/category
              field, so that segment is omitted rather than invented. */}
          <Flex
            align="center"
            gap="8px"
            h="32px"
            px="10px"
            borderRadius="md"
            bg="announcement.chipBg"
          >
            <Box
              aria-hidden="true"
              w="6px"
              h="6px"
              borderRadius="full"
              bg="announcement.accent"
              boxShadow="announcement.dot"
              flexShrink={0}
            />
            <Text
              as="span"
              fontFamily="heading"
              fontSize="12px"
              fontWeight="medium"
              color="announcement.title"
            >
              {t('announcements.product.eyebrow')}
            </Text>
          </Flex>

          {/* Without a visual area the dismiss control has nowhere to sit, so
              it falls back to the header row. */}
          {announcement.visualUrl ? null : (
            <IconButton
              aria-label={t('announcements.product.close')}
              onClick={onDismiss}
              {...dismissButtonStyle}
            >
              <Xmark aria-hidden="true" width="14px" height="14px" />
            </IconButton>
          )}
        </Flex>

        <Box
          opacity={fading ? 0 : 1}
          transition={reduceMotion ? undefined : `opacity ${SLIDE_FADE_MS}ms ease`}
        >
          <Text
            id={titleId}
            as="h2"
            fontFamily="heading"
            fontSize="22px"
            fontWeight="semibold"
            letterSpacing="-0.02em"
            lineHeight="1.2"
            color="announcement.title"
          >
            {content.title}
          </Text>
          <Text
            id={descriptionId}
            mt="9px"
            fontSize="14px"
            fontWeight="light"
            lineHeight="1.55"
            color="announcement.body"
          >
            {content.description}
          </Text>
        </Box>

        {showNavigation || showAction ? (
          <Flex
            align="center"
            justify={showNavigation ? 'space-between' : 'flex-end'}
            gap="12px"
            mt="20px"
          >
            {showNavigation ? (
              <Flex align="center" gap="8px">
                <IconButton
                  aria-label={t('announcements.product.previous')}
                  disabled={atStart}
                  onClick={onPrevious}
                  {...arrowStyle(atStart)}
                >
                  <NavArrowLeft aria-hidden="true" width="14px" height="14px" />
                </IconButton>

                <Flex as="ul" align="center" gap="5px" listStyleType="none" m={0} p={0}>
                  {Array.from({ length: total }, (_, slide) => {
                    const isActive = slide + 1 === position;
                    return (
                      <Box as="li" key={slide} display="flex">
                        <chakra.button
                          type="button"
                          title={t('announcements.product.goTo', { index: slide + 1 })}
                          aria-label={t('announcements.product.goTo', { index: slide + 1 })}
                          aria-current={isActive ? 'true' : undefined}
                          onClick={() => onSelect(slide)}
                          w={isActive ? '20px' : '6px'}
                          h="6px"
                          borderRadius="full"
                          bg={isActive ? 'announcement.pagerActive' : 'announcement.pagerIdle'}
                          transition={reduceMotion ? undefined : 'width .2s, background .2s'}
                          _focusVisible={{ outline: 'none', boxShadow: 'none' }}
                        />
                      </Box>
                    );
                  })}
                </Flex>

                <IconButton
                  aria-label={t('announcements.product.next')}
                  disabled={atEnd}
                  onClick={onNext}
                  {...arrowStyle(atEnd)}
                >
                  <NavArrowRight aria-hidden="true" width="14px" height="14px" />
                </IconButton>

                <Text fontSize="11.5px" color="announcement.counter" ml="2px">
                  {t('announcements.product.position', { current: position, total })}
                </Text>
              </Flex>
            ) : null}

            {showAction ? (
              <Button
                asChild
                h="36px"
                minH="36px"
                px="15px"
                gap="6px"
                borderRadius="md"
                bg="announcement.ctaBg"
                color="announcement.ctaFg"
                fontFamily="heading"
                fontSize="13.5px"
                fontWeight="medium"
                flexShrink={0}
                _hover={{ bg: 'announcement.ctaHoverBg' }}
                _focusVisible={{ outline: 'none', boxShadow: 'none' }}
              >
                {safeInternalLink ? (
                  <RouterLink to={announcement.linkUrl!}>
                    {content.linkLabel}
                    <ArrowRight aria-hidden="true" width="14px" height="14px" />
                  </RouterLink>
                ) : (
                  <a href={announcement.linkUrl!} target="_blank" rel="noopener noreferrer">
                    {content.linkLabel}
                    <ArrowRight aria-hidden="true" width="14px" height="14px" />
                  </a>
                )}
              </Button>
            ) : null}
          </Flex>
        ) : null}
      </Box>
    </Box>
  );
}
